import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  getErrorLoadingNetworkConnectionsByQuery,
  getIsLoadingNetworkConnectionsByQuery,
  getNetworkConnectionsByQuery,
  getNextNetworkConnectionOffsetByQuery,
  getStaleNetworkConnectionsByQuery,
} from "users/networking/_stores/network-connections-redux-slice";
import type NetworkConnectionQuery from "users/networking/connections/_api/queries/network-connection-query";
import {
  type NetworkConnectionPages,
  userNetworkConnectionsApi,
} from "users/networking/connections/_api/user-network-connection-api";

const PAGE_SIZE = 25;

type UseInfiniteNetworkConnectionsOptions = {
  skip?: boolean;
};

/**
 * Loads the caller's network connections one page at a time, for a list that loads more when it
 * scrolls to its end. A new search starts again from the first page.
 *
 * Lists on the same search share the pages held for it: page zero is put in front of them, never
 * in their place, so one list opening does not drop what another has scrolled to. A connection
 * that arrives on no held page refreshes page zero, because only the server can say where it goes.
 */
export default function useInfiniteNetworkConnections(
  query: NetworkConnectionQuery,
  options?: UseInfiniteNetworkConnectionsOptions,
) {
  const skip = options?.skip ?? false;
  const dispatch = useAttorneyHubDispatch();
  const connections = useSelector(getNetworkConnectionsByQuery(query));
  const isLoading = useSelector(getIsLoadingNetworkConnectionsByQuery(query));
  const error = useSelector(getErrorLoadingNetworkConnectionsByQuery(query));
  const nextOffset = useSelector(getNextNetworkConnectionOffsetByQuery(query));
  const stale = useSelector(getStaleNetworkConnectionsByQuery(query));
  const requestsRef = useRef(new Set<{ abort?: () => void; unsubscribe?: () => void }>());
  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const dispatchPage = useCallback(
    (pageIndex: number, pages: NetworkConnectionPages) => {
      const pageQuery = queryRef.current.withPagination(
        PaginationParameters.create({ pageIndex, pageSize: PAGE_SIZE }),
      );
      // RTK Query keeps an answer for the same arguments, and would not ask again. The slice
      // holds the pages, so each ask must reach the server.
      const request = dispatch(
        userNetworkConnectionsApi.endpoints.getUserNetworkConnections.initiate(
          { query: pageQuery, pages },
          { forceRefetch: true },
        ),
      );
      requestsRef.current.add(request);
      void request.finally(() => {
        requestsRef.current.delete(request);
        request.unsubscribe?.();
      });
      return request;
    },
    [dispatch],
  );

  useEffect(() => {
    if (skip) return;
    const request = dispatchPage(0, "refresh");
    return () => {
      // A dialog can re-run this effect as it settles after opening. Do not cancel the
      // initial page in that cleanup: a replacement subscriber can use the same request.
      request.unsubscribe?.();
    };
  }, [dispatchPage, query.cacheKey, skip]);

  useEffect(
    () => () => {
      for (const request of requestsRef.current) {
        // Let RTK Query finish a short list request so a transient dialog unmount does not
        // turn the initial load into an error that requires a manual retry.
        request.unsubscribe?.();
      }
      requestsRef.current.clear();
    },
    [],
  );

  // The count is already above zero when a list opens on a search another list has held. Only a
  // change after this list opened is news to it: its own first page is already on its way.
  const staleRef = useRef(stale);
  useEffect(() => {
    if (stale === staleRef.current) return;
    staleRef.current = stale;
    if (!skip) dispatchPage(0, "refresh");
  }, [dispatchPage, skip, stale]);

  // The page that holds the first row not read yet. A removal moves it back, so the person who
  // moved up a place is read again rather than skipped; the slice drops the repeat.
  const nextPageIndex = Math.floor(nextOffset / PAGE_SIZE);

  const loadMore = useCallback(() => {
    if (skip || isLoading || !connections.hasNextPage) return;
    dispatchPage(nextPageIndex, "next");
  }, [connections.hasNextPage, dispatchPage, isLoading, nextPageIndex, skip]);

  const retry = useCallback(() => {
    if (skip || isLoading) return;
    if (nextOffset > 0) {
      dispatchPage(nextPageIndex, "next");
    } else {
      dispatchPage(0, "refresh");
    }
  }, [dispatchPage, isLoading, nextOffset, nextPageIndex, skip]);

  return useMemo(
    () => ({
      connections,
      isLoading,
      isInitialLoading: isLoading && connections.isEmpty,
      error,
      loadMore,
      retry,
    }),
    [connections, error, isLoading, loadMore, retry],
  );
}
